import { useState, useRef, useCallback, useEffect } from 'react';
import { useDrop } from 'react-dnd';
import { DraggableAppNode } from './DraggableAppNode';
import { ConnectionLine } from './ConnectionLine';
import type { ConnectionPlan, Position } from '../../types';

interface ConnectionCanvasProps {
  plan: ConnectionPlan | null;
  onAppMove: (appId: string, position: Position) => void;
  onConnectionCreate: (fromAppId: string, toAppId: string) => void;
  onConnectionDelete: (connectionId: string) => void;
  readonly?: boolean;
}

export function ConnectionCanvas({
  plan,
  onAppMove,
  onConnectionCreate,
  onConnectionDelete,
  readonly = false
}: ConnectionCanvasProps) {
  const canvasRef = useRef<HTMLDivElement>(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectionStart, setConnectionStart] = useState<string | null>(null);
  const [mousePosition, setMousePosition] = useState<Position>({ x: 0, y: 0 });

  const [{ isOver }, drop] = useDrop({
    accept: 'app',
    drop: (item: { app: any }, monitor) => {
      if (!canvasRef.current) return;
      
      const offset = monitor.getDropResult();
      const canvasRect = canvasRef.current.getBoundingClientRect();
      const clientOffset = monitor.getClientOffset();
      
      if (clientOffset) {
        const position = {
          x: clientOffset.x - canvasRect.left,
          y: clientOffset.y - canvasRect.top
        };
        
        // Add app to canvas at dropped position
        // This would be handled by the parent component
      }
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  });

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (isConnecting && canvasRef.current) {
      const rect = canvasRef.current.getBoundingClientRect();
      setMousePosition({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      });
    }
  }, [isConnecting]);

  const handleStartConnection = useCallback((appId: string) => {
    if (readonly) return;
    setIsConnecting(true);
    setConnectionStart(appId);
  }, [readonly]);

  const handleEndConnection = useCallback((appId: string) => {
    if (readonly || !connectionStart || connectionStart === appId) {
      setIsConnecting(false);
      setConnectionStart(null);
      return;
    }
    
    onConnectionCreate(connectionStart, appId);
    setIsConnecting(false);
    setConnectionStart(null);
  }, [readonly, connectionStart, onConnectionCreate]);

  const handleCanvasClick = useCallback((e: React.MouseEvent) => {
    if (isConnecting) {
      setIsConnecting(false);
      setConnectionStart(null);
    }
  }, [isConnecting]);

  // Cancel connection on escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isConnecting) {
        setIsConnecting(false);
        setConnectionStart(null);
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isConnecting]);

  return (
    <div
      ref={(node) => {
        canvasRef.current = node;
        drop(node);
      }}
      className={`relative h-full bg-gray-50 dark:bg-gray-900 overflow-hidden cursor-${isConnecting ? 'crosshair' : 'default'}`}
      onMouseMove={handleMouseMove}
      onClick={handleCanvasClick}
    >
      {/* Grid Pattern */}
      <div 
        className="absolute inset-0 opacity-30"
        style={{
          backgroundImage: `
            linear-gradient(to right, #e5e7eb 1px, transparent 1px),
            linear-gradient(to bottom, #e5e7eb 1px, transparent 1px)
          `,
          backgroundSize: '20px 20px'
        }}
      />

      {/* Apps */}
      {plan?.apps.map((planApp) => (
        <DraggableAppNode
          key={planApp.app.id}
          app={planApp.app}
          position={planApp.position}
          isConnecting={isConnecting}
          isConnectionStart={connectionStart === planApp.app.id}
          onMove={(position) => onAppMove(planApp.app.id, position)}
          onStartConnection={() => handleStartConnection(planApp.app.id)}
          onEndConnection={() => handleEndConnection(planApp.app.id)}
          readonly={readonly}
        />
      ))}

      {/* Existing Connections */}
      {plan?.connections.map((connection) => {
        const fromApp = plan.apps.find(a => a.app.id === connection.fromAppId);
        const toApp = plan.apps.find(a => a.app.id === connection.toAppId);
        
        if (!fromApp || !toApp) return null;
        
        return (
          <ConnectionLine
            key={connection.id}
            from={fromApp.position}
            to={toApp.position}
            connectionType={connection.type}
            onDelete={() => onConnectionDelete(connection.id)}
            readonly={readonly}
          />
        );
      })}

      {/* Temporary Connection Line */}
      {isConnecting && connectionStart && (
        (() => {
          const startApp = plan?.apps.find(a => a.app.id === connectionStart);
          if (!startApp) return null;
          
          return (
            <ConnectionLine
              from={startApp.position}
              to={mousePosition}
              connectionType="trigger-action"
              isTemporary
            />
          );
        })()
      )}

      {/* Drop Zone Indicator */}
      {isOver && (
        <div className="absolute inset-0 bg-blue-500/10 border-2 border-dashed border-blue-500 flex items-center justify-center pointer-events-none">
          <div className="bg-blue-500 text-white px-6 py-3 rounded-lg font-medium shadow-lg">
            Drop app to add to canvas
          </div>
        </div>
      )}

      {/* Instructions */}
      {plan?.apps.length === 0 && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center text-gray-500 dark:text-gray-400">
            <div className="text-6xl mb-4">🎯</div>
            <h3 className="text-xl font-medium mb-2">Visual Connection Builder</h3>
            <p className="text-sm max-w-md">
              Drag apps from the sidebar to start building your connection flow.
              Click and drag between apps to create connections.
            </p>
          </div>
        </div>
      )}

      {/* Connection Mode Instructions */}
      {isConnecting && (
        <div className="absolute top-4 left-1/2 transform -translate-x-1/2 bg-blue-500 text-white px-4 py-2 rounded-lg shadow-lg z-50">
          Click on another app to create a connection • Press ESC to cancel
        </div>
      )}
    </div>
  );
}